import React from 'react';
import { getCachedImageUrl, releaseCachedImageUrl } from '../utils/imageCache';

type Props = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  lazy?: boolean;
};

const CachedImg: React.FC<Props> = ({ src, alt, className, style, onLoad, onError, loading, lazy = true, ...rest }) => {
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const [displayUrl, setDisplayUrl] = React.useState<string>('');
  const srcRef = React.useRef<string>('');

  const startFetch = React.useCallback(async () => {
    if (!src) return;
    const currentSrc = src;
    srcRef.current = currentSrc;
    try {
      const { url } = await getCachedImageUrl(currentSrc);
      if (srcRef.current === currentSrc) {
        setDisplayUrl(url);
      }
    } catch {
      if (srcRef.current === currentSrc) {
        setDisplayUrl(currentSrc);
      }
    }
  }, [src]);

  React.useEffect(() => {
    setDisplayUrl('');
    // release previous
    return () => {
      if (srcRef.current) {
        try { releaseCachedImageUrl(srcRef.current); } catch {}
      }
    };
  }, [src]);

  React.useEffect(() => {
    if (!lazy) {
      void startFetch();
      return;
    }
    let observer: IntersectionObserver | null = null;
    const el = imgRef.current;
    if (!el) {
      // delay to next microtask when ref attaches
      const id = requestAnimationFrame(() => { if (imgRef.current) void startFetch(); });
      return () => cancelAnimationFrame(id);
    }
    if (typeof IntersectionObserver === 'undefined') {
      void startFetch();
      return;
    }
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (observer) observer.disconnect();
          void startFetch();
        }
      });
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => { if (observer) observer.disconnect(); };
  }, [lazy, startFetch]);

  return (
    <img
      ref={imgRef}
      src={displayUrl || undefined}
      alt={alt}
      className={className}
      style={style}
      loading={loading || 'lazy'}
      decoding="async"
      onLoad={onLoad}
      onError={onError}
      {...rest}
    />
  );
};

export default CachedImg;
